import { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Select({
  field,
  options,
  placeholder = "Choose one",
  error,
  inputSize = "xl",
  testId
}) {
  const sizeClasses = {
    sm: "py-1 text-sm",
    md: "py-1.5 text-sm",
    lg: "py-2 text-base",
    xl: "py-3 text-base",
  };

  return (
    <div className="relative w-full">
      <Listbox value={field.value} onChange={field.onChange}>
        {({ open }) => (
          <>
            <Listbox.Button
              data-testid={testId ? `${testId}-button` : undefined}
              className={classNames(
                "relative w-full cursor-pointer pl-3 pr-10 text-left text-gray-900 sm:text-sm",
                sizeClasses[inputSize],
                "hover:bg-slate-100 focus:outline-none focus:ring-0",
                error && "ring-1 ring-red-300 rounded-md"
              )}
            >
              <span className="flex items-center">
                {field.value?.icon && (
                  <span className="flex-shrink-0 mr-3">{field.value.icon}</span>
                )}
                <span className="block truncate min-h-[22.5px] flex items-center">
                  {field.value ? (
                    field.value.name
                  ) : (
                    <span className="text-gray-400">{placeholder}</span>
                  )}
                </span>
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
              </span>
            </Listbox.Button>

            <Transition
              as={Fragment}
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Listbox.Options
                data-testid={testId ? `${testId}-options` : undefined}
                className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              >
                {options.map((o) => (
                  <Listbox.Option
                    key={o.id}
                    value={o}
                    data-testid={
                      testId ? `${testId}-option-${o.id}` : undefined
                    }
                    className={({ active }) =>
                      classNames(
                        "relative cursor-default select-none pl-3 pr-9",
                        sizeClasses[inputSize],
                        active
                          ? "bg-blue text-white py-2"
                          : "text-gray-900 py-2"
                      )
                    }
                  >
                    {({ selected }) => (
                      <div className="flex items-center">
                        {o.icon && (
                          <span className="flex-shrink-0 mr-3">{o.icon}</span>
                        )}
                        <span
                          className={classNames(
                            selected
                              ? "font-semibold block truncate"
                              : "font-normal block truncate"
                          )}
                        >
                          {o.name}
                        </span>
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
}
